import type { Idioma } from "./idiomas";

/**
 * La pagina de "este negocio es mio", en nueve idiomas.
 *
 * Es el paso de conversion: a esta pagina se llega desde una ficha que puede
 * estar en aleman o en polaco, y encontrarsela en castellano es perder a quien
 * acababa de decidir dar el paso. No se indexa —de ahi que el idioma viaje en
 * la consulta y no en la ruta—, pero tiene que leerse.
 */

export type TextosReclamar = {
  titulo: (nombre: string) => string;
  queConsigues: string;
  ventajas: [string, string, string, string];
  yaTieneDueno: string;
  campoNombre: string;
  campoTelefono: string;
  campoEmail: string;
  campoMensaje: string;
  opcional: string;
  enviar: string;
  enviando: string;
  enviadoTitulo: string;
  enviadoTexto: string;
  volver: string;
  error: string;
};

export const TEXTOS_RECLAMAR: Record<Idioma, TextosReclamar> = {
  es: {
    titulo: (n) => `Reclamar ${n}`,
    queConsigues: "Qué consigues al reclamarla",
    ventajas: [
      "Corriges los datos: dirección, teléfono, horario y web.",
      "Publicas tu carta gratis, con tus platos y tus precios.",
      "Tu negocio sube en el listado de tu municipio: cuanto más completa está la ficha, más arriba sale.",
      "Tus clientes ven la carta antes de entrar, y desde el móvil.",
    ],
    yaTieneDueno: "Esta ficha ya tiene dueño. Si crees que hay un error, escríbenos a",
    campoNombre: "Tu nombre",
    campoTelefono: "Teléfono",
    campoEmail: "Correo electrónico",
    campoMensaje: "Algo que nos ayude a confirmar que es tuyo",
    opcional: "(opcional)",
    enviar: "Reclamar esta ficha",
    enviando: "Enviando…",
    enviadoTitulo: "Petición enviada",
    enviadoTexto:
      "Te escribimos en menos de 24 horas para confirmar que el negocio es tuyo y darte acceso. A partir de ahí puedes crear tu carta y publicarla en el directorio.",
    volver: "Volver a la ficha",
    error: "No se ha podido enviar",
  },
  de: {
    titulo: (n) => `${n} übernehmen`,
    queConsigues: "Was Sie damit bekommen",
    ventajas: [
      "Sie korrigieren die Daten: Adresse, Telefon, Öffnungszeiten und Website.",
      "Sie veröffentlichen Ihre Karte kostenlos, mit Ihren Gerichten und Preisen.",
      "Ihr Lokal steigt in der Liste Ihrer Gemeinde: je vollständiger der Eintrag, desto weiter oben.",
      "Ihre Gäste sehen die Karte, bevor sie hereinkommen, direkt auf dem Handy.",
    ],
    yaTieneDueno:
      "Dieser Eintrag hat bereits einen Inhaber. Wenn Sie meinen, das sei ein Fehler, schreiben Sie uns an",
    campoNombre: "Ihr Name",
    campoTelefono: "Telefon",
    campoEmail: "E-Mail",
    campoMensaje: "Etwas, das uns hilft zu bestätigen, dass es Ihr Lokal ist",
    opcional: "(optional)",
    enviar: "Diesen Eintrag übernehmen",
    enviando: "Wird gesendet…",
    enviadoTitulo: "Anfrage gesendet",
    enviadoTexto:
      "Wir melden uns innerhalb von 24 Stunden, um zu bestätigen, dass das Lokal Ihnen gehört, und geben Ihnen Zugang. Danach können Sie Ihre Karte anlegen und im Verzeichnis veröffentlichen.",
    volver: "Zurück zum Eintrag",
    error: "Konnte nicht gesendet werden",
  },
  en: {
    titulo: (n) => `Claim ${n}`,
    queConsigues: "What claiming it gets you",
    ventajas: [
      "You fix the details: address, phone, opening hours and website.",
      "You publish your menu for free, with your dishes and your prices.",
      "Your business climbs your town's list: the more complete the listing, the higher it shows.",
      "Your customers read the menu before walking in, from their phone.",
    ],
    yaTieneDueno: "This listing already has an owner. If you think that is a mistake, write to us at",
    campoNombre: "Your name",
    campoTelefono: "Phone",
    campoEmail: "Email",
    campoMensaje: "Anything that helps us confirm it is yours",
    opcional: "(optional)",
    enviar: "Claim this listing",
    enviando: "Sending…",
    enviadoTitulo: "Request sent",
    enviadoTexto:
      "We will write to you within 24 hours to confirm the business is yours and give you access. From there you can build your menu and publish it in the directory.",
    volver: "Back to the listing",
    error: "Could not be sent",
  },
  ca: {
    titulo: (n) => `Reclamar ${n}`,
    queConsigues: "Què hi guanyes",
    ventajas: [
      "Corregeixes les dades: adreça, telèfon, horari i web.",
      "Publiques la teva carta gratis, amb els teus plats i els teus preus.",
      "El teu negoci puja al llistat del teu municipi: com més completa està la fitxa, més amunt surt.",
      "Els teus clients veuen la carta abans d'entrar, i des del mòbil.",
    ],
    yaTieneDueno: "Aquesta fitxa ja té amo. Si creus que hi ha un error, escriu-nos a",
    campoNombre: "El teu nom",
    campoTelefono: "Telèfon",
    campoEmail: "Correu electrònic",
    campoMensaje: "Alguna cosa que ens ajudi a confirmar que és teu",
    opcional: "(opcional)",
    enviar: "Reclamar aquesta fitxa",
    enviando: "Enviant…",
    enviadoTitulo: "Petició enviada",
    enviadoTexto:
      "T'escrivim en menys de 24 hores per confirmar que el negoci és teu i donar-te accés. A partir d'aquí pots crear la teva carta i publicar-la al directori.",
    volver: "Tornar a la fitxa",
    error: "No s'ha pogut enviar",
  },
  fr: {
    titulo: (n) => `Revendiquer ${n}`,
    queConsigues: "Ce que vous y gagnez",
    ventajas: [
      "Vous corrigez les informations : adresse, téléphone, horaires et site web.",
      "Vous publiez votre carte gratuitement, avec vos plats et vos prix.",
      "Votre établissement monte dans la liste de votre commune : plus la fiche est complète, plus elle remonte.",
      "Vos clients consultent la carte avant d'entrer, depuis leur téléphone.",
    ],
    yaTieneDueno:
      "Cette fiche a déjà un propriétaire. Si vous pensez qu'il y a une erreur, écrivez-nous à",
    campoNombre: "Votre nom",
    campoTelefono: "Téléphone",
    campoEmail: "Adresse e-mail",
    campoMensaje: "Quelque chose qui nous aide à confirmer que c'est le vôtre",
    opcional: "(facultatif)",
    enviar: "Revendiquer cette fiche",
    enviando: "Envoi…",
    enviadoTitulo: "Demande envoyée",
    enviadoTexto:
      "Nous vous écrivons sous 24 heures pour confirmer que l'établissement est le vôtre et vous donner accès. Ensuite, vous pourrez créer votre carte et la publier dans l'annuaire.",
    volver: "Retour à la fiche",
    error: "L'envoi a échoué",
  },
  it: {
    titulo: (n) => `Rivendica ${n}`,
    queConsigues: "Cosa ottieni rivendicandola",
    ventajas: [
      "Correggi i dati: indirizzo, telefono, orari e sito.",
      "Pubblichi il tuo menu gratis, con i tuoi piatti e i tuoi prezzi.",
      "Il tuo locale sale nell'elenco del tuo comune: più la scheda è completa, più in alto compare.",
      "I tuoi clienti leggono il menu prima di entrare, dal telefono.",
    ],
    yaTieneDueno: "Questa scheda ha già un titolare. Se pensi ci sia un errore, scrivici a",
    campoNombre: "Il tuo nome",
    campoTelefono: "Telefono",
    campoEmail: "Email",
    campoMensaje: "Qualcosa che ci aiuti a confermare che è tuo",
    opcional: "(facoltativo)",
    enviar: "Rivendica questa scheda",
    enviando: "Invio…",
    enviadoTitulo: "Richiesta inviata",
    enviadoTexto:
      "Ti scriviamo entro 24 ore per confermare che il locale è tuo e darti l'accesso. Da lì puoi creare il tuo menu e pubblicarlo nell'elenco.",
    volver: "Torna alla scheda",
    error: "Non è stato possibile inviare",
  },
  nl: {
    titulo: (n) => `${n} claimen`,
    queConsigues: "Wat claimen u oplevert",
    ventajas: [
      "U corrigeert de gegevens: adres, telefoon, openingstijden en website.",
      "U publiceert uw menukaart gratis, met uw gerechten en uw prijzen.",
      "Uw zaak klimt in de lijst van uw gemeente: hoe vollediger de vermelding, hoe hoger.",
      "Uw gasten lezen de kaart voordat ze binnenkomen, vanaf hun telefoon.",
    ],
    yaTieneDueno: "Deze vermelding heeft al een eigenaar. Denkt u dat dat een fout is, mail ons dan op",
    campoNombre: "Uw naam",
    campoTelefono: "Telefoon",
    campoEmail: "E-mailadres",
    campoMensaje: "Iets waarmee we kunnen bevestigen dat het uw zaak is",
    opcional: "(optioneel)",
    enviar: "Deze vermelding claimen",
    enviando: "Versturen…",
    enviadoTitulo: "Aanvraag verstuurd",
    enviadoTexto:
      "We schrijven u binnen 24 uur om te bevestigen dat de zaak van u is en u toegang te geven. Daarna kunt u uw kaart aanmaken en in de gids publiceren.",
    volver: "Terug naar de vermelding",
    error: "Versturen is niet gelukt",
  },
  pl: {
    titulo: (n) => `Przejmij: ${n}`,
    queConsigues: "Co zyskujesz",
    ventajas: [
      "Poprawiasz dane: adres, telefon, godziny otwarcia i stronę.",
      "Publikujesz menu za darmo, ze swoimi daniami i cenami.",
      "Twój lokal idzie w górę listy w swojej gminie: im pełniejsza wizytówka, tym wyżej.",
      "Goście czytają menu, zanim wejdą — prosto z telefonu.",
    ],
    yaTieneDueno: "Ta wizytówka ma już właściciela. Jeśli uważasz, że to pomyłka, napisz do nas na",
    campoNombre: "Twoje imię i nazwisko",
    campoTelefono: "Telefon",
    campoEmail: "E-mail",
    campoMensaje: "Coś, co pomoże nam potwierdzić, że to Twój lokal",
    opcional: "(opcjonalnie)",
    enviar: "Przejmij tę wizytówkę",
    enviando: "Wysyłanie…",
    enviadoTitulo: "Zgłoszenie wysłane",
    enviadoTexto:
      "Napiszemy w ciągu 24 godzin, żeby potwierdzić, że lokal jest Twój, i dać Ci dostęp. Potem możesz ułożyć menu i opublikować je w katalogu.",
    volver: "Wróć do wizytówki",
    error: "Nie udało się wysłać",
  },
  ru: {
    titulo: (n) => `Подтвердить права: ${n}`,
    queConsigues: "Что это вам даёт",
    ventajas: [
      "Вы исправляете данные: адрес, телефон, часы работы и сайт.",
      "Вы бесплатно публикуете меню со своими блюдами и ценами.",
      "Заведение поднимается в списке своего муниципалитета: чем полнее карточка, тем выше.",
      "Гости читают меню ещё до входа, прямо с телефона.",
    ],
    yaTieneDueno: "У этой карточки уже есть владелец. Если это ошибка, напишите нам на",
    campoNombre: "Ваше имя",
    campoTelefono: "Телефон",
    campoEmail: "Электронная почта",
    campoMensaje: "Что-нибудь, что поможет подтвердить, что заведение ваше",
    opcional: "(необязательно)",
    enviar: "Это моё заведение",
    enviando: "Отправка…",
    enviadoTitulo: "Заявка отправлена",
    enviadoTexto:
      "Мы напишем в течение 24 часов, чтобы подтвердить, что заведение ваше, и открыть доступ. После этого вы сможете собрать меню и опубликовать его в каталоге.",
    volver: "Вернуться к карточке",
    error: "Не удалось отправить",
  },
};

export function textosReclamar(idioma: Idioma): TextosReclamar {
  return TEXTOS_RECLAMAR[idioma];
}
